import { CategoryPageBucketEntry } from '../../../models/CategoryPage';
import React, { FC, ReactNode, useCallback } from 'react';
import { Link, To } from 'react-router-dom';
import { cn } from '@bem-react/classname';
import './SidebarList.scss';
import { useTranslation } from 'react-i18next';

const block = cn('sidebar-list');

export type SidebarListItemTargetBuilderFunc = (
    loanFormat: string | null | undefined,
    itemId: string,
    subItemId?: string | null
) => To;

export const SidebarListContainer = ({ children }: { children: ReactNode }) => {
    return <ul className={block()}>{children}</ul>;
};

type SidebarListProps = {
    readonly title: string;
    readonly entries: ReadonlyArray<CategoryPageBucketEntry>;
    readonly selectedEntry: string;
    readonly loanFormatUrlKey: string; // urlKey
    readonly itemTargetBuilder: SidebarListItemTargetBuilderFunc;
};

export const SidebarList: FC<SidebarListProps> = ({
    title,
    entries,
    selectedEntry,
    loanFormatUrlKey,
    itemTargetBuilder
}) => {
    const selectedMainCategory = selectedEntry.split('->')[0];
    const buildCategoryItem = useCallback(
        (entry: CategoryPageBucketEntry, parentId?: string) => (
            <SidebarItem
                key={entry.key}
                id={entry.key}
                parentId={parentId}
                name={entry.value}
                docCount={entry.docCount ?? 0}
                active={entry.key === selectedEntry || entry.key === selectedMainCategory}
                loanFormatUrlKey={loanFormatUrlKey}
                itemTargetBuilder={itemTargetBuilder}
            />
        ),
        [selectedEntry, selectedMainCategory, loanFormatUrlKey, itemTargetBuilder]
    );

    if (entries.length === 0) {
        return null;
    }

    return (
        <div className={block('list')}>
            <div className={block('header')}>{title}</div>
            {entries.map(entry => {
                const isSelectedCategory = selectedMainCategory === entry.key;
                const category = buildCategoryItem(entry);
                const subCategories =
                    isSelectedCategory && entry.buckets
                        ? entry.buckets.map(subEntry => buildCategoryItem(subEntry, entry.key))
                        : [];

                return (
                    <div key={entry.key}>
                        {category}
                        {subCategories}
                    </div>
                );
            })}
        </div>
    );
};

type SidebarItemProps = {
    readonly active: boolean;
    readonly id: string;
    readonly parentId?: string;
    readonly name: string;
    readonly docCount: number;
    readonly loanFormatUrlKey: string;
    readonly itemTargetBuilder: SidebarListItemTargetBuilderFunc;
};

const SidebarItem: FC<SidebarItemProps> = ({
    itemTargetBuilder,
    loanFormatUrlKey,
    active,
    parentId,
    name,
    docCount,
    id
}) => {
    const isSubMenu = !!parentId;
    const { t } = useTranslation();
    const toUrl = isSubMenu
        ? itemTargetBuilder(loanFormatUrlKey, parentId!, id)
        : itemTargetBuilder(loanFormatUrlKey, id);
    // TODO: Refactor Link classname "active" once 003_types.scss is gone (this globally styles the anchor tag -.-)

    return (
        <li className={block('item', { 'sub-item': isSubMenu })}>
            <Link to={toUrl} className={active ? 'active' : 'black'}>
                {name}
                <span className={block('doc-count')}>
                    ({t('numberFormat.numberWithoutDecimalPoint', { val: docCount })})
                </span>
            </Link>
        </li>
    );
};
